import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { COURSE_PRICES } from '../../config/const';
import { Button, Grid, TextField } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
	fetchCourseIntroMessage,
	getCourseInfo,
	setCourseIntroMessage,
	updateCurriculum,
} from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import { handleDeleteCourse } from '../../utils/helper';
import { toast } from 'react-toastify';

const CourseIntro = () => {
	const location = useLocation();
	const { courseId } = useParams();
	const [courseData, setCourseData] = useState(null);
	const [assignments, setAssignments] = useState(null);
	const [introMessage, setIntroMessage] = useState('');
	const [loading, setLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [savedIntroMessage, setSavedIntroMessage] = useState(null);
	const tmpCourseData = useRef(null);
	const navigate = useNavigate();
	const { authUser } = useAuth();
	const shouldDeleteCourseRef = useRef(true);

	useEffect(() => {
		loadCourseData();
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (courseId && courseData) {
			// load assessments while user is working on intro message
			if (!assignments) {
				updateCurriculum(courseId, courseData)
					.then((resp) => {
						setAssignments(resp.modules);
					})
					.catch((error) => {
						console.log(error);
						loadCourseDataFromId();
					});
			}
		}

		return () => {
			if (shouldDeleteCourseRef.current) {
				handleDeleteCourse(courseData, authUser);
			}
		};
	}, [courseId, courseData]); //eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (assignments && savedIntroMessage) {
			shouldDeleteCourseRef.current = false;
			let updatedCurriculum = {
				...courseData,
				intro: introMessage,
			};
			// this step is complete if assignments have been generated and intro message saved
			navigate(`/CreateAssessments/${courseId}`, {
				state: {
					assignments: assignments,
					courseId,
					courseData: updatedCurriculum,
				},
			});
		}
	}, [assignments, savedIntroMessage]); //eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (assignments && tmpCourseData.current) {
			setCourseData(tmpCourseData.current);
			setIntroMessage(tmpCourseData.current?.intro || '');
		}
	}, [assignments]);

	let loadCourseData = async () => {
		if (location?.state?.courseData) {
			setCourseData(location?.state?.courseData);
		} else {
			loadCourseDataFromId();
		}
	};

	let loadCourseDataFromId = async () => {
		let resp = await getCourseInfo(courseId);
		if (resp.ok) {
			let data = await resp.json();
			if (authUser?.documentId === data.creator_id) {
				delete data.creator_avatar;
				if (data?.creation_done) {
					shouldDeleteCourseRef.current = false;
				}
				if (data?.modules && data.modules[0].questions) {
					tmpCourseData.current = data;
					setAssignments(data.modules);
				} else {
					setCourseData(data);
					setIntroMessage(data?.intro || '');
				}
			} else {
				// user is not instructor
				console.log('User is not instructor');
				navigate(-1);
			}
		} else {
			// course not found
			console.log('Course not found');
			navigate(-1);
		}
	};

	let handleGenerateIntro = async () => {
		setLoading(true);
		try {
			let resp = await fetchCourseIntroMessage(courseId, true);
			if (!resp.ok) {
				throw new Error(`HTTP error! Status: ${resp.status}`);
			}
			let output = await resp.json();
			setIntroMessage(output.response);
		} catch (error) {
			console.error('Error generating course introduction: ', error);
		} finally {
			setLoading(false);
		}
	};

	let handleSaveIntro = async () => {
		try {
			if (introMessage.length > 0) {
				setIsSaving(true);
				setCourseIntroMessage(courseId, introMessage).then(() => {
					setSavedIntroMessage(introMessage);
				});
			} else {
				toast.error(
					'Please provide an introduction message to continue'
				);
			}
		} catch (error) {
			console.error('Error saving course introduction: ', error);
		}
	};

	return courseData ? (
		<Grid container spacing={2} sx={{ mt: 0 }}>
			<div style={{ width: '100%' }}>
				<Box
					display='flex'
					justifyContent='center'
					alignItems='center'
					sx={{
						backgroundColor: (theme) =>
							theme.palette.background['paper-elevation-6'],
						py: 1,
						width: '100%',
					}}
				>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }}>
						Create New Course
					</Typography>
				</Box>
			</div>
			<Grid
				container
				spacing={1}
				sx={{ mt: 0 }}
				maxWidth='lg'
				mx='auto'
				textAlign='left'
			>
				<Grid
					item
					xs={12}
					sx={{
						p: 2,
						ml: { xs: 2, md: 0 },
						mr: { xs: 2, md: 0 },
						pb: { xs: 0, md: 2 },
						borderBottom: { md: '1px solid lightgray' },
					}}
				>
					<Typography
						typography={'h6'}
						color='text.primary'
						align='left'
						marginTop={1}
					>
						Course Information
					</Typography>
				</Grid>
				<Grid
					item
					xs={12}
					md={4}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						mb: { xs: 1, md: 4 },
						pt: { xs: 0, md: 2 },
					}}
				>
					<Box
						className='course-details-description-box'
						sx={{
							mt: { xs: 0, md: 1 },
							ml: { xs: 2, md: 0 },
							mr: { xs: 2, md: 0 },
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Subject:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.subject}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Course:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.name}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Instructor:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.creator_name}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Grade:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.grade}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Duration:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.module_count + ' Modules'}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Price:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1 }}
								typography={'body1'}
								color='text.secondary'
							>
								{COURSE_PRICES[courseData?.module_count]}
							</Typography>
						</Box>

						<Box
							className={'course-details-overview-box'}
							sx={{ display: 'flex', mt: 4, mb: 2 }}
						>
							<Typography
								align='left'
								typography={'h6'}
								color='text.primary'
							>
								Overview:
							</Typography>
							<Typography
								align='left'
								sx={{ ml: 1, mt: 0.5 }}
								typography={'body1'}
								color='text.secondary'
							>
								{courseData?.overview}
							</Typography>
						</Box>
					</Box>
					{courseData?.modules.map((mod: any, index: number) => {
						return (
							<Box
								sx={{
									mt: 2,
									ml: { xs: 2, md: 0 },
									mr: { xs: 2, md: 0 },
								}}
								key={`module-${index}`}
							>
								<Typography
									align='left'
									typography={'h6'}
									color='text.primary'
								>
									{`Module ${index + 1}: ${mod.title}`}
								</Typography>
								{mod?.topics.map(
									(topic: any, top_index: number) => {
										return (
											<Typography
												align='left'
												sx={{ ml: 2 }}
												typography={'body1'}
												color='text.secondary'
												key={`module-${index}-topic-${top_index}`}
											>
												{`${top_index + 1}. ${
													topic.name
												}`}
											</Typography>
										);
									}
								)}
							</Box>
						);
					})}
				</Grid>
				<Grid item xs={12} md={8}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							mt: 1,
							ml: { xs: 2, md: 0 },
							mr: 2,
						}}
					>
						<Typography
							align='left'
							typography={'h6'}
							color='text.primary'
						>
							Introduction Message
						</Typography>
						<Typography
							align='left'
							sx={{ mt: 2, mb: 2 }}
							typography={'body1'}
							color='text.secondary'
						>
							This message will be shown to students at the
							beginning of your course. Please give a warm welcome
							and include anything else you wish to share with
							your students to start them on their journey.
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={6}
							variant='outlined'
							value={introMessage}
							onChange={(e) => setIntroMessage(e.target.value)}
						/>
						<Grid container spacing={2} pt={2}>
							<Grid item xs={12} md={6}>
								<Button
									variant='contained'
									color='primary'
									size='large'
									onClick={handleGenerateIntro}
									startIcon={
										loading ? (
											<CircularProgress size={22} />
										) : (
											<AutoAwesomeIcon />
										)
									}
									disabled={loading || isSaving}
									fullWidth
								>
									{loading
										? 'Generating...'
										: 'Generate New Introduction'}
								</Button>
							</Grid>
							<Grid item xs={12} md={6} pb={2}>
								<Button
									variant='contained'
									color='primary'
									size='large'
									onClick={handleSaveIntro}
									startIcon={
										isSaving ? (
											<CircularProgress size={22} />
										) : (
											<></>
										)
									}
									endIcon={
										isSaving ? <></> : <ArrowForwardIcon />
									}
									disabled={
										loading ||
										isSaving ||
										!introMessage.length
									}
									fullWidth
								>
									{isSaving ? 'Saving...' : 'Next'}
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Grid>
			</Grid>
		</Grid>
	) : (
		<Box
			sx={{
				display: 'flex',
				p: 2,
				justifyContent: 'center',
			}}
		>
			<CircularProgress />
		</Box>
	);
};

export default CourseIntro;
